import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Shield, Clock, CheckCircle, XCircle } from 'lucide-react';
import Card from '../../components/Card';
import Table from '../../components/Table';
import Badge from '../../components/Badge';
import Select from '../../components/Select';
import { getAccountTypeApplications } from '../../utils/api';
import { formatDate, getStatusBadgeVariant } from '../../utils/helpers';

export default function VerificationList() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Get initial state from URL
    const urlStatus = searchParams.get('status') || 'pending';
    const [status, setStatus] = useState(urlStatus);

    // Sync URL when status changes
    useEffect(() => {
        const newParams = new URLSearchParams();
        if (status && status !== 'pending') {
            newParams.set('status', status);
        }

        const currentParamsStr = searchParams.toString();
        const newParamsStr = newParams.toString();

        if (currentParamsStr !== newParamsStr) {
            setSearchParams(newParams, { replace: true });
        }
    }, [status, searchParams, setSearchParams]);

    // Sync local state when URL changes (back navigation)
    useEffect(() => {
        const newStatus = searchParams.get('status') || 'pending';
        if (newStatus !== status) {
            setStatus(newStatus);
        }
    }, [searchParams]);

    const handleStatusChange = useCallback((newStatus) => {
        setStatus(newStatus);
    }, []);

    // Fetch ALL applications for this type (no status filter) - filter client-side
    const { data, isLoading } = useQuery({
        queryKey: ['verification-applications-all'],
        queryFn: () => getAccountTypeApplications({ status: 'all', accountType: 'verified' }),
    });

    const allApplications = data?.data || [];

    // Calculate stats from ALL applications (not filtered)
    const stats = {
        pending: allApplications.filter(a => a.status === 'pending').length,
        approved: allApplications.filter(a => a.status === 'approved').length,
        rejected: allApplications.filter(a => a.status === 'rejected').length,
    };

    // Filter for display based on selected status
    const applications = status === 'all'
        ? allApplications
        : allApplications.filter(a => a.status === status);

    const columns = [
        {
            header: 'User',
            accessor: 'username',
            render: (app) => (
                <div className="flex items-center gap-3">
                    {app.user?.profilePicture ? (
                        <img
                            src={app.user.profilePicture}
                            alt={app.user.username}
                            className="w-10 h-10 rounded-full object-cover"
                        />
                    ) : (
                        <div className="w-10 h-10 rounded-full bg-accent-light dark:bg-accent-dark flex items-center justify-center">
                            <span className="font-medium">
                                {app.user?.username?.charAt(0)?.toUpperCase()}
                            </span>
                        </div>
                    )}
                    <div>
                        <p className="font-medium">@{app.user?.username}</p>
                        <p className="text-sm text-muted-light dark:text-muted-dark">
                            {app.user?.displayName}
                        </p>
                    </div>
                </div>
            ),
        },
        {
            header: 'Method',
            accessor: 'verificationMethod',
            render: (app) => (
                <span className="capitalize text-sm">{app.verificationMethod}</span>
            ),
        },
        {
            header: 'Status',
            accessor: 'status',
            render: (app) => (
                <Badge variant={getStatusBadgeVariant(app.status)}>
                    {app.status?.replace('_', ' ')}
                </Badge>
            ),
        },
        {
            header: 'Submitted',
            accessor: 'createdAt',
            render: (app) => <span className="text-sm">{formatDate(app.createdAt)}</span>,
        },
        {
            header: 'Reviewed',
            accessor: 'reviewedAt',
            render: (app) => (
                <span className="text-sm">
                    {app.reviewedAt ? formatDate(app.reviewedAt) : '-'}
                </span>
            ),
        },
    ];

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-3xl font-bold mb-2">Verification Requests</h1>
                <p className="text-muted-light dark:text-muted-dark">
                    Review and approve verification applications
                </p>
            </div>

            {/* Status Filter Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card
                    className={`cursor-pointer transition-all ${status === 'pending' ? 'ring-2 ring-primary' : 'hover:bg-accent-light/50 dark:hover:bg-accent-dark/50'}`}
                    onClick={() => handleStatusChange('pending')}
                >
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-yellow-500/10">
                            <Clock size={18} className="text-yellow-500" />
                        </div>
                        <div>
                            <p className="text-xs text-muted-light dark:text-muted-dark">Pending</p>
                            <p className="text-xl font-bold">{stats.pending}</p>
                        </div>
                    </div>
                </Card>
                <Card
                    className={`cursor-pointer transition-all ${status === 'approved' ? 'ring-2 ring-primary' : 'hover:bg-accent-light/50 dark:hover:bg-accent-dark/50'}`}
                    onClick={() => handleStatusChange('approved')}
                >
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-green-500/10">
                            <CheckCircle size={18} className="text-green-500" />
                        </div>
                        <div>
                            <p className="text-xs text-muted-light dark:text-muted-dark">Approved</p>
                            <p className="text-xl font-bold">{stats.approved}</p>
                        </div>
                    </div>
                </Card>
                <Card
                    className={`cursor-pointer transition-all ${status === 'rejected' ? 'ring-2 ring-primary' : 'hover:bg-accent-light/50 dark:hover:bg-accent-dark/50'}`}
                    onClick={() => handleStatusChange('rejected')}
                >
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-red-500/10">
                            <XCircle size={18} className="text-red-500" />
                        </div>
                        <div>
                            <p className="text-xs text-muted-light dark:text-muted-dark">Rejected</p>
                            <p className="text-xl font-bold">{stats.rejected}</p>
                        </div>
                    </div>
                </Card>
            </div>

            <Card>
                <div className="mb-6">
                    <Select
                        value={status}
                        onChange={(e) => handleStatusChange(e.target.value)}
                        className="w-48"
                    >
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                    </Select>
                </div>

                <Table
                    columns={columns}
                    data={applications}
                    loading={isLoading}
                    onRowClick={(app) => navigate(`/verifications/${app._id}`)}
                    emptyState={{
                        title: 'No verification requests',
                        description: `No ${status} requests found`,
                    }}
                />
            </Card>
        </div>
    );
}
